import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { MessagesSquare, X, ArrowUp, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SESSION_KEY = "mp-advisor-session";
const WHATSAPP = "917624853840";
const HIDDEN_ROUTES = ["/admin", "/login", "/dashboard"];

const PROMPTS = [
  "Do you have a navy in 600?",
  "Onion colour ke shade number kya hain?",
  "Rama green shade ಯಾವುದು?",
  "Closest shade to Pantone 19-4052",
];

const newSessionId = () =>
  `adv-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

const readSession = () => {
  try {
    const existing = localStorage.getItem(SESSION_KEY);
    if (existing) return existing;
    const fresh = newSessionId();
    localStorage.setItem(SESSION_KEY, fresh);
    return fresh;
  } catch (e) {
    console.debug("Advisor session could not be persisted:", e);
    return newSessionId();
  }
};

const GREETING = {
  role: "assistant",
  content:
    "Namaskara. I'm the shade advisor for Mutha Paras & Co. Tell me the colour you need — in English, Hindi or Kannada — and I'll point you to the shade numbers on our card.",
};

export default function ShadeAdvisor() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([GREETING]);
  const [draft, setDraft] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const sessionRef = useRef(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (!open || loaded) return;
    sessionRef.current = sessionRef.current || readSession();
    fetch(`${API}/shade-advisor/history/${sessionRef.current}`)
      .then((r) => r.json())
      .then((data) => {
        if (data.messages?.length) setMessages([GREETING, ...data.messages]);
      })
      .catch((e) => console.debug("Advisor history load failed:", e))
      .finally(() => setLoaded(true));
  }, [open, loaded]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, streaming]);

  const send = useCallback(
    async (text) => {
      const question = text.trim();
      if (!question || streaming) return;
      sessionRef.current = sessionRef.current || readSession();
      setDraft("");
      setMessages((prev) => [...prev, { role: "user", content: question }, { role: "assistant", content: "" }]);
      setStreaming(true);

      try {
        const res = await fetch(`${API}/shade-advisor/chat`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ session_id: sessionRef.current, message: question }),
        });
        if (!res.ok || !res.body) throw new Error(`advisor ${res.status}`);

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        let done = false;
        while (!done) {
          const { value, done: finished } = await reader.read();
          if (finished) break;
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n\n");
          buffer = lines.pop() || "";
          for (const line of lines) {
            if (!line.startsWith("data: ")) continue;
            const evt = JSON.parse(line.slice(6));
            if (evt.type === "delta") {
              setMessages((prev) => {
                const next = [...prev];
                next[next.length - 1] = {
                  role: "assistant",
                  content: next[next.length - 1].content + evt.content,
                };
                return next;
              });
            } else if (evt.type === "error") {
              setMessages((prev) => {
                const next = [...prev];
                next[next.length - 1] = { role: "assistant", content: evt.content };
                return next;
              });
              done = true;
            } else if (evt.type === "done") {
              done = true;
            }
          }
        }
      } catch (err) {
        console.debug("Advisor request failed:", err);
        setMessages((prev) => {
          const next = [...prev];
          next[next.length - 1] = {
            role: "assistant",
            content: "I couldn't reach the shade card just now. Please message us on WhatsApp and we'll help right away.",
          };
          return next;
        });
      } finally {
        setStreaming(false);
      }
    },
    [streaming]
  );

  const shadesMentioned = [
    ...new Set(
      messages
        .filter((m) => m.role === "assistant")
        .flatMap((m) => m.content.match(/#\d{1,3}\b/g) || [])
    ),
  ].slice(0, 8);

  const whatsappHref = `https://wa.me/${WHATSAPP}?text=${encodeURIComponent(
    shadesMentioned.length
      ? `Hello Mutha Paras & Co., your shade advisor suggested ${shadesMentioned.join(", ")}. Please confirm availability and rates.`
      : "Hello Mutha Paras & Co., I was using the shade advisor on your website and would like some help."
  )}`;

  if (HIDDEN_ROUTES.some((r) => pathname.startsWith(r))) return null;

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="shade-advisor-launcher"
          aria-label="Ask the shade advisor"
          className="fixed bottom-[5.5rem] right-4 sm:bottom-24 sm:right-6 z-50 flex items-center gap-2 text-xs sm:text-sm font-medium pl-3 pr-4 py-2.5 sm:pl-4 sm:pr-5 sm:py-3 rounded-full transition-transform duration-300 hover:scale-105"
          style={{
            backgroundColor: "var(--mp-copper-solid)",
            color: "#FFFFFF",
            fontFamily: "'Manrope', sans-serif",
            boxShadow: "0 4px 20px rgba(160, 95, 40, 0.35)",
          }}
        >
          <MessagesSquare className="w-5 h-5" />
          <span className="hidden sm:inline">Ask for a shade</span>
        </button>
      )}

      {open && (
        <div
          data-testid="shade-advisor-panel"
          className="fixed inset-x-3 bottom-3 sm:inset-x-auto sm:right-6 sm:bottom-6 sm:w-[390px] z-50 flex flex-col rounded-sm overflow-hidden border"
          style={{
            backgroundColor: "var(--mp-card)",
            borderColor: "var(--mp-line)",
            maxHeight: "min(78vh, 620px)",
            boxShadow: "0 24px 60px rgba(16, 16, 16, 0.28)",
          }}
        >
          <div
            className="flex items-start justify-between gap-3 px-5 py-4"
            style={{ backgroundColor: "var(--mp-ink)" }}
          >
            <div>
              <p
                className="text-[10px] tracking-[0.18em] uppercase mb-1"
                style={{ color: "var(--mp-copper)", fontFamily: "'Manrope', sans-serif" }}
              >
                Shade Advisor
              </p>
              <h2
                className="text-lg leading-tight"
                style={{ fontFamily: "'Cormorant Garamond', serif", color: "#FAF7F0" }}
              >
                Find your shade number
              </h2>
            </div>
            <button
              onClick={() => setOpen(false)}
              data-testid="shade-advisor-close"
              aria-label="Close shade advisor"
              className="p-1.5 rounded-sm transition-opacity hover:opacity-70"
              style={{ color: "#FAF7F0" }}
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto px-4 py-4 space-y-3"
            style={{ backgroundColor: "var(--mp-surface)" }}
            data-testid="shade-advisor-messages"
          >
            {messages.map((m, i) => (
              <div
                key={`${m.role}-${i}`}
                className={`max-w-[88%] px-3.5 py-2.5 rounded-sm text-sm whitespace-pre-wrap ${
                  m.role === "user" ? "ml-auto" : "mr-auto"
                }`}
                style={{
                  backgroundColor: m.role === "user" ? "var(--mp-solid)" : "var(--mp-card)",
                  color: m.role === "user" ? "var(--mp-on-solid)" : "var(--mp-text)",
                  border: m.role === "user" ? "none" : "1px solid var(--mp-line)",
                  fontFamily: "'Manrope', sans-serif",
                  lineHeight: 1.6,
                }}
                data-testid={`advisor-msg-${m.role}-${i}`}
              >
                {m.content || (
                  <Loader2
                    className="w-3.5 h-3.5 animate-spin"
                    style={{ color: "var(--mp-copper)" }}
                  />
                )}
              </div>
            ))}

            {messages.length === 1 && (
              <div className="pt-2 space-y-2" data-testid="advisor-prompt-chips">
                {PROMPTS.map((p) => (
                  <button
                    key={p}
                    onClick={() => send(p)}
                    className="block w-full text-left text-xs px-3 py-2 rounded-sm border transition-colors hover:border-[var(--mp-copper)]"
                    style={{
                      borderColor: "var(--mp-line)",
                      backgroundColor: "var(--mp-card)",
                      color: "var(--mp-muted)",
                      fontFamily: "'Manrope', sans-serif",
                    }}
                    data-testid={`advisor-chip-${p.slice(0, 12).toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {p}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div
            className="px-4 py-3 border-t"
            style={{ borderColor: "var(--mp-line)", backgroundColor: "var(--mp-card)" }}
          >
            <form
              onSubmit={(e) => {
                e.preventDefault();
                send(draft);
              }}
              className="flex items-center gap-2"
            >
              <input
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                maxLength={500}
                placeholder="Which colour do you need?"
                data-testid="shade-advisor-input"
                className="flex-1 text-sm bg-transparent outline-none py-2"
                style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-text)" }}
              />
              <button
                type="submit"
                disabled={streaming || !draft.trim()}
                data-testid="shade-advisor-send"
                aria-label="Send question"
                className="p-2 rounded-full transition-opacity disabled:opacity-40"
                style={{ backgroundColor: "var(--mp-copper-solid)", color: "#FFFFFF" }}
              >
                {streaming ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <ArrowUp className="w-4 h-4" />
                )}
              </button>
            </form>
            <a
              href={whatsappHref}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="shade-advisor-whatsapp"
              className="block mt-1 text-[11px] transition-opacity hover:opacity-70"
              style={{ color: "var(--mp-copper-deep)", fontFamily: "'Manrope', sans-serif" }}
            >
              Shades only — for rates, stock and orders, continue on WhatsApp &rarr;
            </a>
          </div>
        </div>
      )}
    </>
  );
}
